import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { GoogleMap, Marker, Polyline } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { Box, Skeleton, useTheme } from '@mui/material';
import { TripEventDto } from '@acorn-portal/shared/src/services/telematicsDriverTripsApi';
import { useInViewport } from '@acorn-portal/shared/src/components/telematics/common/use-in-viewport';
import { useGoogleMapsApi } from './use-google-maps-api';
import { buildAlgorithm, buildRenderer, getEventColor } from './trip-event-utils';
import {
	buildCircleMarkerIcon,
	buildMarkerLabel,
	decodeEventPolylines,
	decodePolyline,
	fitMapToPoints,
	getEventCoordinates,
} from './trip-map-utils';

/**
 * Props for the TripFullMap component.
 */
interface TripFullMapProps {
	/** Encoded polyline string for the trip route. */
	polyline?: string;
	/** Array of trip events to display as markers on the map. */
	events?: TripEventDto[];
	/** Callback fired when an event marker is clicked. */
	onEventClick?: (eventId: string) => void;
	/** Height of the map container in pixels. Default: 300 */
	height?: number;
	/** Clustering algorithm: 'supercluster' | 'grid' | 'noop'. Default: 'supercluster' */
	clusterAlgorithm?: 'supercluster' | 'grid' | 'noop';
}

const MAP_PADDING = 30;
/** Liverpool; shown only when a trip has no route polyline. */
const FALLBACK_CENTRE: google.maps.LatLngLiteral = { lat: 53.4084, lng: -2.9916 };

const MAP_OPTIONS: google.maps.MapOptions = {
	disableDefaultUI: true,
	zoomControl: true,
	gestureHandling: 'greedy', // Allow zoom without the ctrl+scroll prompt
	clickableIcons: false,
	streetViewControl: false,
	mapTypeControl: false,
	fullscreenControl: false,
	scaleControl: false,
};

const HIDE_MAP_CHROME_SX = {
	'& .gm-style-cc': { display: 'none' },
	'& a[href^="https://maps.google.com/maps"]': { display: 'none !important' },
	'& .gm-style > div:last-child': { display: 'none' },
} as const;

/**
 * Interactive Google Map displaying a trip route with event markers.
 *
 * Features include:
 * - Full trip route polyline with start (A) and end (B) markers
 * - Event polylines coloured by severity overlaid on the route
 * - Numbered, clustered event markers positioned at polyline midpoints
 * - Lazy loading: the map mounts the first time it scrolls into view
 * - Click interaction to select events and sync with the events list
 *
 * @component
 * @example
 * ```tsx
 * <TripFullMap
 *   polyline={tripData.polyline}
 *   events={tripEvents}
 *   onEventClick={(eventId) => setHighlightedEvent(eventId)}
 *   height={400}
 * />
 * ```
 */
const TripFullMap: React.FC<TripFullMapProps> = ({
	polyline,
	events,
	onEventClick,
	height = 300,
	clusterAlgorithm = 'supercluster',
}) => {
	const theme = useTheme();
	const { isLoaded } = useGoogleMapsApi();
	const { ref: containerRef, isInViewport } = useInViewport({ rootMargin: '100px', once: true });

	const mapRef = useRef<google.maps.Map | null>(null);
	const clustererRef = useRef<MarkerClusterer | null>(null);

	const canDecode = isLoaded && isInViewport;
	const routePath = useMemo(() => (canDecode ? decodePolyline(polyline) : []), [canDecode, polyline]);
	const eventPolylines = useMemo(() => (canDecode ? decodeEventPolylines(events) : []), [canDecode, events]);
	const boundsPoints = useMemo(
		() => (routePath.length > 0 ? [...routePath, ...getEventCoordinates(events)] : []),
		[routePath, events],
	);

	const onMapLoad = useCallback(
		(map: google.maps.Map) => {
			mapRef.current = map;
			clustererRef.current = new MarkerClusterer({
				map,
				algorithm: buildAlgorithm(clusterAlgorithm),
				renderer: buildRenderer(theme),
			});
			fitMapToPoints(map, boundsPoints, MAP_PADDING);
		},
		[boundsPoints, clusterAlgorithm, theme],
	);

	// Re-fit if the route or events change after the map has loaded
	useEffect(() => {
		if (mapRef.current) fitMapToPoints(mapRef.current, boundsPoints, MAP_PADDING);
	}, [boundsPoints]);

	// Tear down the clusterer with the component
	useEffect(() => {
		return () => {
			clustererRef.current?.clearMarkers();
			clustererRef.current?.setMap(null);
		};
	}, []);

	const routeColour = theme.palette.text.primary;
	const hasRoute = routePath.length > 0;
	const isWaitingForRoute = Boolean(polyline) && !hasRoute;

	if (!isInViewport || !isLoaded || isWaitingForRoute) {
		return (
			<Box ref={containerRef} sx={{ width: '100%', height, borderRadius: 2, overflow: 'hidden' }}>
				<Skeleton variant='rectangular' width='100%' height='100%' />
			</Box>
		);
	}

	const startPoint = hasRoute ? routePath[0] : null;
	const endPoint = hasRoute ? routePath[routePath.length - 1] : null;

	return (
		<Box ref={containerRef} sx={{ width: '100%', height, borderRadius: 2, overflow: 'hidden', ...HIDE_MAP_CHROME_SX }}>
			<GoogleMap
				mapContainerStyle={{ width: '100%', height: '100%' }}
				zoom={12}
				center={startPoint ?? FALLBACK_CENTRE}
				onLoad={onMapLoad}
				options={MAP_OPTIONS}
			>
				{hasRoute && (
					<Polyline path={routePath} options={{ strokeColor: routeColour, strokeOpacity: 1, strokeWeight: 3 }} />
				)}

				{eventPolylines.map((eventPolyline) => (
					<Polyline
						key={`polyline-${eventPolyline.eventId}`}
						path={eventPolyline.path}
						options={{
							strokeColor: getEventColor(eventPolyline.severity, theme),
							strokeOpacity: 1,
							strokeWeight: 5,
							zIndex: 10,
						}}
					/>
				))}

				{startPoint && (
					<Marker
						position={startPoint}
						label={buildMarkerLabel('A')}
						icon={buildCircleMarkerIcon(routeColour)}
						zIndex={1}
					/>
				)}
				{endPoint && (
					<Marker
						position={endPoint}
						label={buildMarkerLabel('B')}
						icon={buildCircleMarkerIcon(routeColour)}
						zIndex={1}
					/>
				)}

				{eventPolylines.map((eventPolyline) => (
					<Marker
						key={`marker-${eventPolyline.eventId}`}
						position={eventPolyline.midpoint}
						label={buildMarkerLabel(String(eventPolyline.index + 1), '10px')}
						icon={buildCircleMarkerIcon(getEventColor(eventPolyline.severity, theme))}
						onClick={() => onEventClick?.(eventPolyline.eventId)}
						zIndex={20}
						onLoad={(marker) => {
							clustererRef.current?.addMarker(marker, false);
							clustererRef.current?.render();
						}}
						onUnmount={(marker) => {
							clustererRef.current?.removeMarker(marker, false);
							clustererRef.current?.render();
						}}
					/>
				))}
			</GoogleMap>
		</Box>
	);
};

export default TripFullMap;
